import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AtSign, Lock, Loader2, ArrowLeft, Building2, ShieldCheck, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { homeFor, ROLES } from "@/lib/roles";
import LanguageToggle from "@/components/layout/LanguageToggle";

const HERO_IMAGE =
    "https://images.pexels.com/photos/3844581/pexels-photo-3844581.jpeg?auto=compress&cs=tinysrgb&w=1400";

const PORTAL_FEATURES = [
    { icon: Building2,   en: "Provider tools",    ar: "أدوات المزودين"   },
    { icon: ShieldCheck, en: "Admin controls",    ar: "لوحة الإدارة"     },
    { icon: Users,       en: "Team management",   ar: "إدارة الفريق"     },
];

export default function StaffLoginPage() {
    const { user, login, logout } = useAuth();
    const { lang, isRTL } = useI18n();
    const location = useLocation();

    const [identifier, setIdentifier] = useState("");
    const [password,   setPassword]   = useState("");
    const [submitting, setSubmitting]  = useState(false);
    const [error,      setError]       = useState("");

    const tr = (en, ar) => lang === "ar" ? ar : en;

    // Already logged in as staff — go to their dashboard
    if (user && user.role && user.role !== ROLES.MEMBER) {
        const next = location.state?.from || homeFor(user.role);
        return <Navigate to={next} replace />;
    }

    // Already logged in as member — send them to member area
    if (user && user.role === ROLES.MEMBER) {
        return <Navigate to={homeFor(ROLES.MEMBER)} replace />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);

        const res = await login(identifier.trim().toLowerCase(), password);
        setSubmitting(false);

        if (!res.ok) {
            if (res.status === 429) {
                setError(tr("Too many attempts. Please wait 15 minutes.", "عدد كبير من المحاولات. يرجى الانتظار ١٥ دقيقة."));
            } else {
                setError(tr("Incorrect email or password.", "البريد الإلكتروني أو كلمة المرور غير صحيحة."));
            }
            return;
        }

        // Block members from accessing the staff portal
        if (res.user?.role === ROLES.MEMBER) {
            await logout();
            setError(tr(
                "This portal is for staff only. Members can sign in at the member login page.",
                "هذه البوابة مخصصة للموظفين فقط. يمكن للأعضاء تسجيل الدخول من صفحة العضوية."
            ));
            return;
        }
    };

    return (
        <div className="min-h-screen w-full flex" dir={isRTL ? "rtl" : "ltr"} data-testid="staff-login-page">

            {/* ── Left / Form panel ─────────────────────────────────── */}
            <div className="relative flex flex-col w-full md:w-1/2 shrink-0 bg-background">

                {/* Top bar */}
                <div className="flex items-center justify-between px-8 pt-7">
                    <a
                        href="/home"
                        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        {tr("Back to site", "العودة إلى الموقع")}
                    </a>
                    <LanguageToggle />
                </div>

                {/* Form area */}
                <div className="flex-1 flex flex-col justify-center px-8 sm:px-14 lg:px-20 py-10">
                    <div className="w-full max-w-md mx-auto">

                        {/* Logo + heading */}
                        <div className="mb-8">
                            <img src="/hemaia_logo.svg" alt="Hemaia" className="h-10 mb-7" />
                            <div className="inline-flex items-center gap-2 bg-primary/8 text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                                <ShieldCheck className="h-3.5 w-3.5" />
                                {tr("Staff Portal", "بوابة الموظفين")}
                            </div>
                            <h1 className="text-2xl font-semibold tracking-tight">
                                {tr("Sign in to your account", "تسجيل الدخول إلى حسابك")}
                            </h1>
                            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                                {tr(
                                    "For admins, providers, and staff members.",
                                    "للمسؤولين ومزودي الخدمة وأعضاء الفريق."
                                )}
                            </p>
                        </div>

                        {/* Form */}
                        <form onSubmit={onSubmit} className="space-y-4" noValidate>
                            <div>
                                <label htmlFor="identifier" className="block text-sm font-medium mb-1.5">
                                    {tr("Email or username", "البريد الإلكتروني أو اسم المستخدم")}
                                </label>
                                <div className="relative">
                                    <AtSign className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <input
                                        id="identifier"
                                        type="text"
                                        autoComplete="username"
                                        required
                                        autoFocus
                                        value={identifier}
                                        onChange={(e) => setIdentifier(e.target.value)}
                                        className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow"
                                        data-testid="staff-login-identifier"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium mb-1.5">
                                    {tr("Password", "كلمة المرور")}
                                </label>
                                <div className="relative">
                                    <Lock className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <input
                                        id="password"
                                        type="password"
                                        autoComplete="current-password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow"
                                        data-testid="staff-login-password"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div
                                    className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive"
                                    data-testid="staff-login-error"
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting || !identifier || !password}
                                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-95 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-1"
                                data-testid="staff-login-submit"
                            >
                                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                {submitting ? tr("Signing in…", "جارٍ تسجيل الدخول…") : tr("Sign in", "تسجيل الدخول")}
                            </button>

                            <div className="text-center">
                                <a
                                    href="/forgot-password"
                                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {tr("Forgot your password?", "نسيت كلمة المرور؟")}
                                </a>
                            </div>
                        </form>

                        {/* Cross-links */}
                        <div className="mt-6 pt-6 border-t border-border/50 text-center space-y-2">
                            <p className="text-sm text-muted-foreground">
                                {tr("Not a staff member?", "لست موظفاً؟")}{" "}
                                <a
                                    href="/login"
                                    className="text-primary font-semibold hover:underline underline-offset-2"
                                >
                                    {tr("Member login →", "تسجيل دخول الأعضاء →")}
                                </a>
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {tr("New provider?", "مزود خدمة جديد؟")}{" "}
                                <a
                                    href="/provider-signup"
                                    className="text-muted-foreground font-medium hover:text-foreground hover:underline underline-offset-2"
                                >
                                    {tr("Register your clinic →", "سجّل عيادتك →")}
                                </a>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <p className="text-center text-xs text-muted-foreground/40 pb-6 px-8">
                    © {new Date().getFullYear()} Hemaia Health Network
                </p>
            </div>

            {/* ── Right / Image panel ───────────────────────────────── */}
            <div className="hidden md:flex relative flex-1 overflow-hidden">

                <img
                    src={HERO_IMAGE}
                    alt="Medical team"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    loading="eager"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/5" />

                <div className="absolute bottom-0 start-0 end-0 p-10 text-white">
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-4">
                        {tr("Hemaia — Staff Portal", "حماية — بوابة الموظفين")}
                    </p>

                    <h2 className="text-3xl lg:text-4xl font-bold leading-tight">
                        {tr("Manage your clinic\nfrom one place", "أدر عيادتك\nمن مكان واحد")}
                    </h2>

                    <p className="mt-3 text-sm text-white/65 max-w-sm leading-relaxed">
                        {tr(
                            "Access your provider dashboard, manage offers, track visits, and verify member cards — all in one portal.",
                            "صل إلى لوحة مزودك، أدر العروض، تابع الزيارات، وتحقق من بطاقات الأعضاء — كل ذلك في بوابة واحدة."
                        )}
                    </p>

                    <div className="flex flex-wrap gap-2 mt-5">
                        {PORTAL_FEATURES.map(({ icon: Icon, en, ar }) => (
                            <div
                                key={en}
                                className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-3.5 py-1.5 text-white text-xs font-medium"
                            >
                                <Icon className="h-3.5 w-3.5 text-white/80 shrink-0" />
                                {lang === "ar" ? ar : en}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

        </div>
    );
}
